<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery, useMutation, useLazyQuery } from '@vue/apollo-composable';
import {
  UserDocument,
  IamUserRolesDocument,
  UserQuery,
  UserRoleType,
  IamUserRoleAssignDocument,
  IamUserRoleRevokeDocument,
} from '@/gql/graphql';
import { deepParseDates, formatDateTime } from '@/utils/date-utils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  BAlert,
  BBadge,
  BButton,
  BFormCheckbox,
  BSpinner,
} from 'bootstrap-vue-next';
import { openRouterLink } from '@/utils/router-utils';

const route = useRoute();

// Compute the user ID by converting the route param to a number
const userId = computed(() => {
  const id = route.params.id as string;
  const userId = Number(id);
  if (isNaN(userId)) {
    throw new Error('Invalid user ID');
  }
  return userId;
});
const userEmail = computed(() => userQueryResult.value?.user?.email ?? '');

// --- USER QUERY ---
const {
  result: userQueryResult,
  loading: userQueryInProgress,
  error: userQueryError,
  refetch: refetchUser,
} = useQuery(
  UserDocument,
  { id: userId.value },
  {
    enabled: computed(() => Boolean(userId.value)),
  }
);

const userData = computed<NonNullable<UserQuery['user']> | null>(() => {
  console.debug('fetched user:', userQueryResult.value?.user);
  if (userQueryResult.value?.user) {
    return deepParseDates(userQueryResult.value.user) as NonNullable<
      UserQuery['user']
    >;
  }
  return null;
});

// --- USER ROLES QUERY ---
const {
  load: fetchUserRoles,
  result: userRolesQueryResult,
  loading: userRolesQueryInProgress,
  error: userRolesQueryError,
} = useLazyQuery(IamUserRolesDocument);

const userRoles = computed(() => {
  if (userRolesQueryResult.value?.iamUserRoles) {
    return userRolesQueryResult.value.iamUserRoles.clients;
  }
  return null;
});

function requestRefetchUserRoles() {
  if (!userEmail.value) {
    console.error('User email is not available for refetching user roles');
    return;
  }
  // Failures show through userRolesQueryError, not as unhandled rejections.
  const request = fetchUserRoles(IamUserRolesDocument, {
    userEmail: userEmail.value,
  });
  if (request) request.catch(() => undefined);
}

// --- ASSIGN NEW User Role ---
const assignRoleMutationError = ref<string | null>(null);
const assignRoleMutationInProgress = ref(false);
const assignRoleMutationShowSuccessIndicator = ref(false);

const {
  mutate: assignRoleMutate,
  onDone: onAssignRoleDone,
  onError: onAssignRoleError,
} = useMutation(IamUserRoleAssignDocument, {
  refetchQueries: [
    {
      query: IamUserRolesDocument,
      variables: { userEmail: userEmail.value },
    },
  ],
  variables: {
    clientId: '',
    userEmail: userEmail.value,
    userRole: '',
  },
});

onAssignRoleDone((res) => {
  if (res.data && res.data.iamUserRoleAssign) {
    assignRoleMutationError.value = null;
    assignRoleMutationShowSuccessIndicator.value = true;

    setTimeout(() => {
      assignRoleMutationShowSuccessIndicator.value = false;
    }, 2000);
  } else {
    console.error('Assigning new user role failed:', res.data);
    assignRoleMutationError.value =
      'Assigning new user role failed: ' + res.data;
  }
  assignRoleMutationInProgress.value = false;
});

onAssignRoleError((err) => {
  assignRoleMutationError.value = err.message;
  assignRoleMutationInProgress.value = false;
});

function handleAddUserRole(clientId: string, newRole: string) {
  if (!userEmail.value) {
    console.error('User email is not available for adding roles');
    assignRoleMutationError.value =
      'User email is not available for adding roles';
    return;
  }

  if (!newRole) {
    console.error('New role is not defined');
    assignRoleMutationError.value = 'New role is not defined';
    return;
  }

  assignRoleMutationError.value = null;
  assignRoleMutationShowSuccessIndicator.value = false;
  assignRoleMutationInProgress.value = true;

  assignRoleMutate({
    clientId: clientId,
    userEmail: userEmail.value,
    userRole: newRole,
  });
}

// --- REVOKE User Role ---
const revokeRoleMutationError = ref<string | null>(null);
const revokeRoleMutationInProgress = ref(false);
const revokeRoleMutationShowSuccessIndicator = ref(false);

const {
  mutate: revokeRoleMutate,
  onDone: onRevokeRoleDone,
  onError: onRevokeRoleError,
} = useMutation(IamUserRoleRevokeDocument, {
  refetchQueries: [
    {
      query: IamUserRolesDocument,
      variables: { userEmail: userEmail.value },
    },
  ],
  variables: {
    clientId: '',
    userEmail: userEmail.value,
    userRole: '',
  },
});

onRevokeRoleDone((res) => {
  if (res.data && res.data.iamUserRoleRevoke) {
    revokeRoleMutationError.value = null;
    revokeRoleMutationShowSuccessIndicator.value = true;

    setTimeout(() => {
      revokeRoleMutationShowSuccessIndicator.value = false;
    }, 2000);
  } else {
    console.error('Revoking user role failed:', res.data);
    revokeRoleMutationError.value = 'Revoking user role failed: ' + res.data;
  }
  revokeRoleMutationInProgress.value = false;
});

onRevokeRoleError((err) => {
  revokeRoleMutationError.value = err.message;
  revokeRoleMutationInProgress.value = false;
});

function handleRevokeUserRole(clientId: string, userRole: string) {
  if (!userEmail.value) {
    console.error('User email is not available for revoking roles');
    revokeRoleMutationError.value =
      'User email is not available for revoking roles';
    return;
  }

  revokeRoleMutationError.value = null;
  revokeRoleMutationShowSuccessIndicator.value = false;
  revokeRoleMutationInProgress.value = true;

  revokeRoleMutate({
    clientId: clientId,
    userEmail: userEmail.value,
    userRole: userRole,
  });
}

// --- Other functions ---
function handleToggleUserRole(
  event: Event,
  clientId: string,
  userRole: UserRoleType
) {
  if (!userEmail.value) {
    console.error('User email is not available for revoking roles');
    revokeRoleMutationError.value =
      'User email is not available for revoking roles';
    event.preventDefault();
    return;
  }
  if (!clientId) {
    console.error('Client ID is not defined');
    revokeRoleMutationError.value = 'Client ID is not defined';
    event.preventDefault();
    return;
  }
  if (!userRole) {
    console.error('User role is not defined');
    revokeRoleMutationError.value = 'User role is not defined';
    event.preventDefault();
    return;
  }

  if (userRole.assigned) {
    // do not permit revocation if only one role is assigned with the current client
    const client = userRoles.value?.find((client) => client?.id === clientId);
    if (client && client.roles && client.roles.length === 1) {
      revokeRoleMutationError.value =
        'Cannot revoke the only role assigned to this client';
      event.preventDefault();
      return;
    }
    if (userRole.default) {
      console.error('Cannot revoke default role');
      revokeRoleMutationError.value = 'Cannot revoke default role';
      event.preventDefault();
      return;
    }
    // revoke the role
    handleRevokeUserRole(clientId, userRole.name);
  } else {
    handleAddUserRole(clientId, userRole.name);
  }
}

watch(
  userEmail,
  (email) => {
    if (email) {
      requestRefetchUserRoles();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- Link back to project list -->
    <b-button
      @click="openRouterLink({ name: 'AdminUserList' }, $router, $event)"
      variant="outline-dark"
      class="mb-3"
      size="sm"
    >
      <font-awesome-icon icon="angle-left" class="me-2" />
      Back to Users
    </b-button>

    <!-- User Detail Header -->
    <h1>
      <font-awesome-icon :icon="['fas', 'user']" />
      <span v-if="userData?.givenName && userData.familyName" class="ms-1"
        >{{ userData.givenName }} {{ userData.familyName }}</span
      >
      <span v-else class="ms-1">User details</span>
    </h1>
    <hr />

    <!-- User Query Error -->
    <b-alert v-model="userQueryError" variant="danger" dismissible class="mt-3">
      {{ userQueryError }}
      <b-button @click="refetchUser" variant="outline-dark" class="btn-sm">
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </b-alert>

    <!-- Loading User Details Spinner -->
    <div v-if="userQueryInProgress" class="loading-spinner">
      Loading... <b-spinner small />
    </div>

    <!-- User Details -->
    <div v-if="userData">
      <!-- Personal details -->
      <p>
        <strong>ID:</strong> {{ userData.id }} <br />
        <strong>UUID:</strong> {{ userData.uuid }} <br />
        <strong>E-mail: </strong> {{ userData.email }} <br />
        <strong>Created: </strong> {{ formatDateTime(userData.createdAt) }}
        <br />
      </p>

      <!-- Organization memberships -->
      <div
        v-if="
          userData.organizationsAssociated &&
          userData.organizationsAssociated.length === 0
        "
      >
        <hr />
        <h2>
          <font-awesome-icon :icon="['fas', 'building']" />
          No organization associations
        </h2>
      </div>
      <div v-else>
        <hr />
        <h2>
          <font-awesome-icon :icon="['fas', 'building']" />
          Organization associations
        </h2>
        <template
          v-for="(assoc, idx) in userData.organizationsAssociated"
          :key="idx"
        >
          <template v-if="assoc?.organization">
            <h5>
              {{ assoc.organization.name }}
              <BBadge v-if="!assoc.removedAt" variant="success" class="ms-2">
                still active
              </BBadge>
              <BBadge v-else variant="secondary" class="ms-2">
                inactive
              </BBadge>
            </h5>
            <p>
              <strong>Role:</strong> {{ assoc.userRole }} <br />
              <strong>From:</strong> {{ formatDateTime(assoc.createdAt) }}
              <br />
              <span v-if="assoc.removedAt">
                <strong>To:</strong>
                {{ formatDateTime(assoc.removedAt) }} <br />
              </span>
              <strong>ID:</strong> {{ assoc.organization.id }} <br />
              <strong>SFCD ID:</strong> {{ assoc.organization.salesforceId }}
              <br />
              <strong>Street:</strong> {{ assoc.organization.addressStreet }}
              <br />
              <strong>City:</strong> {{ assoc.organization.addressCity }} <br />
              <strong>Postal code:</strong>
              {{ assoc.organization.addressPostalCode }} <br />
              <strong>State:</strong> {{ assoc.organization.addressState }}
              <br />
              <strong>Country code:</strong>
              {{ assoc.organization.addressCountryCode }} <br />
            </p>
          </template>
        </template>
      </div>
    </div>
    <!-- User Details end -->

    <!-- User Roles Query Error -->
    <b-alert
      v-model="userRolesQueryError"
      variant="danger"
      dismissible
      class="mt-3"
    >
      {{ userRolesQueryError?.message }}
      <b-button
        @click="requestRefetchUserRoles"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon icon="sync" />
        Retry
      </b-button>
    </b-alert>

    <!-- Loading User Roles Spinner -->
    <div v-if="userRolesQueryInProgress" class="loading-spinner">
      Loading user roles... <b-spinner small />
    </div>

    <!-- User Roles -->
    <div v-if="userRoles">
      <hr />
      <h2>
        <font-awesome-icon :icon="['fas', 'user-shield']" />
        User roles
      </h2>
      <template v-for="(client, idx) in userRoles" :key="idx">
        <template v-if="!client?.name.startsWith('$') && client?.roles">
          <h5 class="mt-3">{{ client?.name }}</h5>
          <p v-if="client?.description">
            {{ client?.description }}
          </p>
          <template v-for="(role, idx) in client?.roles" :key="idx">
            <BFormCheckbox
              v-if="client.id && role"
              size="sm"
              variant="outline-danger"
              class="ms-2"
              switch
              :checked="role.assigned"
              @click="
                handleToggleUserRole($event, client.id, role as UserRoleType)
              "
            >
              <strong>{{ role?.name }} &nbsp;</strong>
              <small>{{ role?.description }}</small>
            </BFormCheckbox>
          </template>
        </template>
      </template>

      <!-- ASSIGN role error-->
      <BAlert
        v-model="assignRoleMutationError"
        variant="danger"
        dismissible
        class="mt-3"
      >
        {{ assignRoleMutationError }}
      </BAlert>

      <!-- REVOKE role error-->
      <BAlert
        v-model="revokeRoleMutationError"
        variant="danger"
        dismissible
        class="mt-3"
      >
        {{ revokeRoleMutationError }}
      </BAlert>
    </div>
  </div>
</template>

<style scoped></style>
